import React from 'react';
import { displayName } from 'features/evals3/editor/graphModel';
import AnswersView from 'shared/decision/AnswersView';

/**
 * One result's path, read top to bottom: each step with a dot for how it
 * went, what it returned, and what it took. The order is the story; nothing
 * says "ran" or "then", because the next row is what came next.
 *
 * The same list draws a saved result (journal rows: `output`, `finished_at`)
 * and a test run in the editor (preview steps: `value`, `details`, `state`),
 * so a test looks exactly like the result it would have been.
 */

const DOT = {
  ok: 'bg-success',
  errored: 'bg-danger',
  pending: 'bg-info',
  running: 'border border-[color:var(--border-strong)] bg-transparent',
};

export const VERDICT_DOT = {
  passed: 'bg-success',
  failed: 'bg-danger',
  errored: 'bg-warning',
  waiting: 'bg-info',
};

export function outcomeOf(run) {
  if (run.state === 'resolved') return run.passed ? 'passed' : 'failed';
  if (run.state === 'errored') return 'errored';
  return 'waiting';
}
const NOT_AN_END = new Set(['stopped', 'not_ready', 'pending', 'running']);

function fmt(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(Number(v.toFixed(3)));
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function ms(n) {
  if (n == null) return null;
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)} s` : `${Math.round(n)} ms`;
}

export function usd(n) {
  const v = Number(n);
  if (!v) return null;
  return v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
}

function stepState(s) {
  if (s.error || s.state === 'errored') return 'errored';
  if (s.state === 'pending' || s.state === 'not_ready') return 'pending';
  if (s.finished_at === null) return 'running';   // a journal row still open
  return 'ok';
}
/** What a step returned and said, whichever shape it came in. */
function readStep(s) {
  const value = s.value !== undefined ? s.value : s.output?.value;
  const details = s.details !== undefined && s.details !== null ? s.details : s.output?.details;
  return { value, details: details || null };
}

/** What a Semantic check found, in one line, and which claims it could not support. */
function SemanticFindings({ details: d }) {
  if (!d || !d.check) return null;
  const pct = (x) => `${Math.round(x * 100)}%`;
  if (d.check === 'grounded') {
    const parts = [`${d.supported} of ${d.claims} claims supported (needs ${pct(d.threshold)})`];
    if (d.spans_matched != null) parts.push(d.spans_used < d.spans_matched ? `newest ${d.spans_used} of ${d.spans_matched} spans` : `${d.spans_used} span${d.spans_used === 1 ? '' : 's'}`);
    if (d.claims_total > d.claims) parts.push(`first ${d.claims} of ${d.claims_total} claims`);
    return (
      <div className="mt-1 text-[11.5px] text-[color:var(--text-secondary)]">
        <div>{parts.join(' · ')}</div>
        {Array.isArray(d.unsupported) && d.unsupported.length > 0 && (
          <details className="mt-1">
            <summary className="cursor-pointer text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-secondary)]">Not supported ({d.unsupported.length})</summary>
            <ul className="mt-1 space-y-1">
              {d.unsupported.map((u, i) => (
                <li key={i} className="flex gap-2 text-[11.5px]">
                  <span className={`shrink-0 font-mono tabular-nums ${u.contradicted ? 'text-danger' : 'text-[color:var(--text-muted)]'}`} title={u.contradicted ? 'contradicted by the context' : 'not found in the context'}>
                    {u.contradicted ? 'contra' : u.basis === 'numbers' ? 'figure' : u.entail.toFixed(2)}
                  </span>
                  <span className="min-w-0 text-[color:var(--text-primary)]">
                    {u.claim}
                    {Array.isArray(u.missing) && u.missing.length > 0 && <span className="text-[color:var(--text-muted)]"> · not in the context: {u.missing.join(', ')}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    );
  }
  const read = d.read_all === false && d.words_total ? ` · read ${Number(d.words_read).toLocaleString()} of ${Number(d.words_total).toLocaleString()} words` : '';
  const vs = typeof d.probability === 'number' ? `${d.probability.toFixed(2)} (true at ${Number(d.threshold).toFixed(2)})` : '';
  if (d.check === 'intent' || d.check === 'statement') {
    const r = d.reads_as || { kind: 'text' };
    const not = r.negated ? 'not ' : '';
    const how = { answered: `read as: ${not}answered`, repeated: `read as: ${not}asked again` }[r.kind];
    const rule = { asks_back: 'the reply opens by asking back', cannot_answer: 'the reply opens by saying it cannot' }[d.not_answering];
    const p = d.pair;
    const found = (r.negated ? 1 - d.probability : d.probability) >= 0.5;   // the repeat was found, however it is asked
    const quote = 'line-clamp-2 border-l-2 border-[color:var(--border-strong)] pl-2 text-[color:var(--text-muted)]';
    return (
      <div className="mt-1 space-y-1 text-[11.5px] text-[color:var(--text-secondary)]">
        <div>“{d.intent ?? d.statement}”{how ? ` · ${how}` : ''} · {vs}{read}</div>
        {r.kind === 'answered' && (
          <>
            <div>
              {d.exchanges > 1 ? `weakest of ${d.exchanges} exchanges · ` : ''}relevance {Number(d.relevance).toFixed(2)}{rule ? ` · ${rule}` : ''}{d.empty_result ? ' · the reply reports an empty result, which answers it' : ''}
            </div>
            {d.exchanges > 1 && d.question && <div className={quote}>Asked: {d.question}</div>}
            {rule && d.first_sentence && <div className={quote}>{d.first_sentence}</div>}
          </>
        )}
        {r.kind === 'repeated' && (
          <>
            {d.turns != null && <div>{d.turns} user turn{d.turns === 1 ? '' : 's'}{d.turns_compared != null && d.turns_compared < d.turns ? `, latest ${d.turns_compared} compared` : ''}</div>}
            {p && found && (
              <div className="space-y-0.5 border-l-2 border-[color:var(--border-strong)] pl-2 text-[color:var(--text-muted)]">
                <div className="line-clamp-1">Turn {p.earlier + 1}: {p.earlier_text}</div>
                <div className="line-clamp-1">Turn {p.later + 1}: {p.later_text}</div>
              </div>
            )}
          </>
        )}
        {r.kind === 'text' && d.evidence && d.probability >= d.threshold && <div className={quote}>{d.evidence}</div>}
      </div>
    );
  }
  if (d.check === 'says') {
    return (
      <div className="mt-1 space-y-1 text-[11.5px] text-[color:var(--text-secondary)]">
        <div>
          “{d.statement}” · {Number(d.entail).toFixed(2)} (needs {Number(d.threshold).toFixed(2)})
          {d.read_all === false && d.words_total ? ` · read the last ${d.words_read.toLocaleString()} of ${d.words_total.toLocaleString()} words` : ''}
        </div>
        {d.evidence && Number(d.entail) >= Number(d.threshold) && (
          <div className="line-clamp-2 border-l-2 border-[color:var(--border-strong)] pl-2 text-[color:var(--text-muted)]">{d.evidence}</div>
        )}
      </div>
    );
  }
  return null;
}

/** A decision model's answers with their probabilities, read against the step's questions when they still match. */
function DecisionAnswers({ answers, node }) {
  const questions = node?.config?.questions;
  const fits = questions && Object.keys(answers).every((k) => questions[k]);
  return <div className="mt-1.5"><AnswersView questions={fits ? questions : null} answers={answers} /></div>;
}

function Output({ value }) {
  if (value == null) return null;
  const entries = typeof value === 'object' && !Array.isArray(value) ? Object.entries(value) : [['value', value]];
  if (!entries.length) return null;
  return (
    <dl className="mt-1 flex flex-wrap gap-1">
      {entries.map(([k, v]) => (
        <div key={k} className="inline-flex max-w-full items-baseline gap-1 rounded border border-[color:var(--border-subtle)] px-1.5 py-0.5 font-mono text-[11px]">
          <dt className="text-[color:var(--text-muted)]">{k}</dt>
          <dd className="min-w-0 truncate text-[color:var(--text-primary)]" title={fmt(v)}>{fmt(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** What ran, in words — the same names the palette uses. */
const KIND = { python: 'Python', llm_judge: 'LLM judge', decision_model: 'Decision model', cross_encoder: 'Semantic check', bi_encoder: 'Semantic check', ui_filter: 'Filter', human: 'Human review' };

export function Step({ step, graph, last, extra = null }) {
  const state = stepState(step);
  const out = readStep(step);
  const reasoning = out.details?.reasoning;
  const cost = usd(step.provider_cost_usd);
  const took = ms(step.latency_ms != null ? Number(step.latency_ms) : null);
  const ctx = step.context && Object.keys(step.context).length ? step.context : null;
  return (
    <li className="relative flex gap-3 pb-3">
      {!last && <span aria-hidden className="absolute left-[4.5px] top-4 bottom-0.5 w-px bg-[color:var(--border-strong)]" />}
      <span aria-hidden className={`relative mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full ${DOT[state]}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-[color:var(--text-primary)]"
            title={step.edge_rule && step.edge_rule !== 'default' ? `Went on because ${step.edge_rule}` : undefined}>
            {displayName(graph, step.node_id)}
            {KIND[step.instrument] && <span className="ml-1.5 text-[11px] font-normal text-[color:var(--text-muted)]">{KIND[step.instrument]}</span>}
            {state === 'errored' && <span className="sr-only"> (errored)</span>}
            {state === 'pending' && <span className="sr-only"> ({step.state === 'not_ready' ? 'not ready' : 'waiting for a person'})</span>}
          </span>
          <span className="shrink-0 whitespace-nowrap text-[11px] tabular-nums text-[color:var(--text-muted)]">
            {[took, step.tokens_in != null ? `${Number(step.tokens_in) + Number(step.tokens_out || 0)} tok` : null, cost].filter(Boolean).join(' · ')}
          </span>
        </div>
        {step.error
          ? <pre className="mt-1 whitespace-pre-wrap rounded border border-danger bg-danger-soft px-2 py-1 font-mono text-[11px] text-danger">{step.error}</pre>
          : <Output value={out.value} />}
        {step.state === 'not_ready' && <p className="mt-1 text-[11.5px] text-warning">Sandbox not ready. Try again in a moment.</p>}
        {step.state === 'pending' && Array.isArray(step.options) && step.options.length > 0 && (
          <p className="mt-1 text-[11.5px] text-[color:var(--text-secondary)]">A grader would choose one of: {step.options.join(', ')}</p>
        )}
        {reasoning && <p className="mt-1 whitespace-pre-wrap text-[11.5px] leading-relaxed text-[color:var(--text-secondary)]">{reasoning}</p>}
        {!step.error && <SemanticFindings details={out.details} />}
        {!step.error && out.details?.answers && <DecisionAnswers answers={out.details.answers} node={graph?.nodes?.[step.node_id]} />}
        {!step.error && out.details?.op === 'similarity' && out.details?.similarity != null && (
          <div className="mt-1 text-[11.5px] text-[color:var(--text-secondary)]">
            similarity {Number(out.details.similarity).toFixed(2)} (needs {Number(out.details.threshold).toFixed(2)}){out.details.embedded_for_test ? ' · embedded for this test' : ''}
          </div>
        )}
        {ctx && (
          <details className="mt-1">
            <summary className="cursor-pointer text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-secondary)]">Passed on: {Object.keys(ctx).join(', ')}</summary>
            <pre className="mt-1 max-h-48 overflow-auto rounded border border-[color:var(--border-subtle)] p-2 font-mono text-[10.5px] text-[color:var(--text-secondary)]">{JSON.stringify(ctx, null, 2)}</pre>
          </details>
        )}
        {extra}
      </div>
    </li>
  );
}

/**
 * `stepExtra(step, i)` renders under a step — the results page puts the
 * per-step "should have been" control there; the editor passes nothing.
 */
export default function ResultPath({ run, steps, graph, queue, stepExtra = null }) {
  const outcome = outcomeOf(run);
  const end = (run.path || [])[run.path?.length - 1];
  const endIsStep = (steps || []).some((s) => s.node_id === end);
  return (
    <ol aria-label="Path" className="text-[12px]">
      {(steps || []).map((s, i) => (
        <Step key={s.id ?? s.seq ?? i} step={s} graph={graph} last={i === steps.length - 1 && outcome === 'waiting' && !queue && run.state !== 'stopped'}
          extra={stepExtra ? stepExtra(s, i) : null} />
      ))}
      {run.state === 'stopped' && (
        <li className="flex gap-3">
          <span aria-hidden className="mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full border border-[color:var(--border-strong)] bg-transparent" />
          <span className="text-[12.5px] text-[color:var(--text-muted)]">Stopped here. The rest of the eval did not run.</span>
        </li>
      )}
      {outcome === 'waiting' && queue?.status === 'open' && (
        <li className="relative flex gap-3 pb-3">
          <span aria-hidden className={`mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full ${VERDICT_DOT.waiting}`} />
          <span className="text-[12.5px] text-[color:var(--text-secondary)]">Waiting for a person</span>
        </li>
      )}
      {outcome !== 'waiting' && !NOT_AN_END.has(run.state) && (
        <li className="flex gap-3">
          <span aria-hidden className={`mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full ${VERDICT_DOT[outcome]}`} />
          <div className="min-w-0">
            <span className={`text-[12.5px] font-semibold ${outcome === 'passed' ? 'text-success' : outcome === 'failed' ? 'text-danger' : 'text-warning'}`}>
              {outcome === 'passed' ? 'Pass' : outcome === 'failed' ? 'Fail' : 'Errored'}
            </span>
            {run.reason_code && <span className="ml-1.5 font-mono text-[11px] text-[color:var(--text-secondary)]">{run.reason_code}</span>}
            {end && !endIsStep && displayName(graph, end) !== run.reason_code && <span className="ml-1.5 text-[11px] text-[color:var(--text-muted)]">· {displayName(graph, end)}</span>}
            {run.state_reason && <div className="mt-0.5 text-[11.5px] text-[color:var(--text-secondary)]">{run.state_reason}</div>}
          </div>
        </li>
      )}
    </ol>
  );
}
